import { EyeIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/chariot-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { Table } from '@/components/table/Table';
import { Container } from '@/components/ui';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useSortableColumn } from '@/hooks/useSortableColumn';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { CustomerActions } from '@/sections/customerManagement/components/actions/CustomerActions';
import { CustomerManagementSearchFilters } from '@/sections/customerManagement/components/CustomerManagementSearchFilters';
import { AddCustomerModal } from '@/sections/customerManagement/components/modals';
import {
  getColumns,
  getSortableColumnsConfig,
} from '@/sections/customerManagement/config/columnDefinitions';
import { CUSTOMER_TABLE_CONFIG } from '@/sections/customerManagement/config/columns';
import { useCustomerManagement } from '@/sections/customerManagement/hooks/useCustomerManagement';
import { CustomerData } from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { encode } from '@/utils/encrypt.util';

const CustomerTableComponent: React.FC = () => {
  const { startImpersonation, isPraetorianUser } = useAuth();
  const [addCustomerModal, setAddCustomerModal] = useState(false);
  const queryClient = useQueryClient();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [sort, setSort] = useState<{
    orderBy: keyof CustomerData;
    descending: boolean;
  }>({
    orderBy: 'displayName',
    descending: false,
  });

  const { drawerState } = useGlobalState();
  const pendingDrawerEmailRef = useRef<string | null>(null);

  const handleCustomerClick = useCallback(
    (customer: CustomerData) => {
      drawerState.handleOpenCustomerDrawer(customer);
    },
    [drawerState]
  );

  const handleCustomerOpenInNewTab = useCallback((customer: CustomerData) => {
    const url = generatePath(`/:userId/${AuthenticatedRoute.INSIGHTS}`, {
      userId: encode(customer.email),
    });
    window.open(url, '_blank');
  }, []);

  const handleViewAccount = useCallback(
    (customer: CustomerData) => {
      startImpersonation(customer.email, AuthenticatedRoute.INSIGHTS);
    },
    [startImpersonation]
  );

  // Add this new handler after handleViewAccount
  const handleRowClickWithModifiers = useCallback(
    (
      customer: CustomerData,
      idx: number,
      event?: React.MouseEvent<HTMLTableRowElement>
    ) => {
      // Check for cmd/ctrl key for "open in new tab"
      if (event?.metaKey || event?.ctrlKey) {
        handleCustomerOpenInNewTab(customer);
      } else {
        handleCustomerClick(customer);
      }
    },
    [handleCustomerClick, handleCustomerOpenInNewTab]
  );

  const { defaultColumns, defaultConfig } = useMemo(() => {
    const columns = getColumns({
      onViewAccount: handleViewAccount,
      isPraetorianUser,
    });

    return {
      defaultColumns: columns,
      defaultConfig: getSortableColumnsConfig({ defaultColumns: columns }),
    };
  }, [handleViewAccount, isPraetorianUser]);

  const { activeColumns, setActiveColumns, columns } = useSortableColumn({
    key: CUSTOMER_TABLE_CONFIG.storageKey,
    defaultColumns,
    defaultConfig,
  });

  const {
    filters,
    setFilters,
    customers,
    allCustomers, // Raw unfiltered customer list
    customersStatus,
    totalCustomers,
  } = useCustomerManagement();

  const handleCustomerCreated = useCallback(
    async (createdEmail: string) => {
      try {
        // Wait for BOTH queries to complete BEFORE setting pending state
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: getQueryKey.getMy('account'),
          }),
          queryClient.refetchQueries({
            queryKey: getQueryKey.getMy(
              'setting',
              undefined,
              undefined,
              undefined,
              undefined,
              true
            ),
          }),
        ]);

        // Clear search filter so new customer is visible in table
        setFilters(prev => ({ ...prev, search: '' }));

        // NOW set pending email - both queries complete, filters cleared
        pendingDrawerEmailRef.current = createdEmail;
      } catch (error) {
        console.error('Failed to refetch customer data:', error);
      }
    },
    [queryClient, setFilters]
  );

  // Open drawer when a newly created customer appears in the list
  useEffect(() => {
    if (!pendingDrawerEmailRef.current) return;

    // Use allCustomers (unfiltered) to avoid race condition with filter clearing
    const customer = allCustomers.find(
      c => c.email === pendingDrawerEmailRef.current
    );

    if (customer) {
      // Open drawer on settings tab for immediate configuration
      drawerState.handleOpenCustomerDrawer(customer, {
        initialTab: 'settings',
      });
      pendingDrawerEmailRef.current = null;
      return;
    }

    // Fallback: Clear pending state after 5 seconds
    const pendingEmail = pendingDrawerEmailRef.current;
    const timeoutId = setTimeout(() => {
      console.warn(`Customer ${pendingEmail} not found after 5 seconds`);
      pendingDrawerEmailRef.current = null;
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [allCustomers, drawerState]);

  // Calculate if filters return empty results when data exists
  const isFilteredEmpty =
    customersStatus === 'success' &&
    customers.length === 0 &&
    totalCustomers > 0;

  // Force table remount when transitioning from empty filtered state to data state
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    customers.length
  );

  // Sort customers based on current sort state
  const sortedCustomers = useMemo(() => {
    if (!sort) return customers;

    return [...customers].sort((a, b) => {
      const aValue = a[sort.orderBy];
      const bValue = b[sort.orderBy];

      // Handle undefined/null values
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue
          .toLowerCase()
          .localeCompare(bValue.toLowerCase());
        return sort.descending ? -comparison : comparison;
      }

      // Number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort.descending ? bValue - aValue : aValue - bValue;
      }

      // Default comparison (convert to string)
      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return sort.descending ? -comparison : comparison;
    });
  }, [customers, sort]);

  // Memoize row actions to prevent unnecessary re-renders
  const rowActions = useCallback(
    (customer: CustomerData) => {
      return {
        menu: {
          items: [
            {
              label: 'View Account',
              icon: <EyeIcon className="size-5" />,
              onClick: () => {
                startImpersonation(customer.email);
              },
            },
          ],
        },
      };
    },
    [startImpersonation]
  );

  return (
    <>
      <Container className="bg-chariot-background-primary/60 border-chariot-stroke flex h-full flex-1 flex-col rounded-md border-2 p-6 pb-0">
        <div className="flex h-full w-full flex-col gap-4">
          <div className="flex w-full flex-row items-center justify-between">
            <CustomerManagementSearchFilters
              filters={filters}
              setFilters={setFilters}
            />
            {isPraetorianUser && (
              <Button
                size="sm"
                styleType="primary"
                startIcon={<PlusCircleIcon className="size-5" />}
                label="Add Customer"
                onClick={() => setAddCustomerModal(true)}
              />
            )}
          </div>

          {/* Customer Actions Toolbar */}
          {selectedRows.length > 0 && (
            <CustomerActions
              customers={sortedCustomers}
              selectedRows={selectedRows}
              onClearSelection={() => setSelectedRows([])}
            />
          )}

          <div className="flex min-h-0 flex-1 flex-col gap-4 pb-6">
            <div className="flex-1 overflow-auto">
              <Table
                key={tableKey}
                name="customer-management"
                rowHeight={CUSTOMER_TABLE_CONFIG.defaultRowHeight}
                columns={columns}
                sortableColumns={{
                  activeColumns,
                  setActiveColumns,
                  defaultConfig,
                }}
                data={sortedCustomers}
                error={null}
                status={customersStatus}
                loadingRowCount={3}
                noData={{
                  title: 'No customers found',
                }}
                selection={{
                  value: selectedRows,
                  onChange: setSelectedRows,
                  selectAll: true,
                }}
                rowActions={rowActions}
                sort={{
                  orderBy: sort.orderBy,
                  descending: sort.descending,
                  onSort: newSort => setSort(newSort),
                }}
                onRowClick={handleRowClickWithModifiers}
              />
            </div>
          </div>
        </div>
      </Container>

      <AddCustomerModal
        isOpen={addCustomerModal}
        onClose={() => setAddCustomerModal(false)}
        onSuccess={handleCustomerCreated}
      />
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const CustomerTable = React.memo(CustomerTableComponent);
